/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React from "react";

interface LifecycleEvent {
  date: string;
  event: string;
  user: string;
  status: string;
}

interface MediaPowderSpecificationsProps {
  lotData: any;
  selectedLotSummary: any;
  lifecycleEvents: LifecycleEvent[];
  batchInfo: any;
  t: (key: string) => string;
}

const MediaPowderSpecifications: React.FC<MediaPowderSpecificationsProps> = ({
  lotData,
  selectedLotSummary,
  lifecycleEvents,
  batchInfo,
  t
}) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-10">
          {/* Lot Summary Card */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-4 py-4 border-b border-slate-100 bg-slate-50/30 flex items-center gap-3">
              <span className="material-symbols-outlined text-orange-500 font-black">{t('mediaPowder.icons.inventory2')}</span>
              <h3 className="text-xs font-black text-slate-800 uppercase tracking-widest">{t('mediaPowder.lotDetails.specifications')}</h3>
            </div>
            <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="space-y-1">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t('mediaPowder.lotDetails.batchName')}</p>
                <p className="text-sm font-black text-slate-700">{batchInfo?.batch_name || "-"}</p>
              </div>
              {/* <div className="space-y-1">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t('mediaPowder.lotDetails.grnNumber')}</p>
                <p className="text-sm font-black text-slate-700">{lotData?.grn_number || "-"}</p>
              </div> */}
              <div className="space-y-1">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t('mediaPowder.lotDetails.manufacturingDate')}</p>
                <p className="text-sm font-black text-slate-700">{batchInfo?.manufacture_date || "-"}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t('mediaPowder.lotDetails.expiryDate')}</p>
                <p className="text-sm font-black text-red-500">{batchInfo?.expiry_date || "-"}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t('mediaPowder.lotDetails.shelfLife')}</p>
                <p className="text-sm font-black text-slate-700">{batchInfo?.shelf_life_days || "-"}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t('mediaPowder.lotDetails.phDetails')}</p>
                <p className="text-sm font-black text-slate-700">{batchInfo?.final_ph? `${batchInfo.final_ph} ± ${batchInfo.final_ph_tolerance ?? 0}` : "-"}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t('mediaPowder.lotDetails.catalogNumber')}</p>
                <p className="text-sm font-black text-slate-700">{batchInfo?.catalog_number || "-"}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t('mediaPowder.lotDetails.storageCondition')}</p>
                <p className="text-sm font-black text-slate-700">{batchInfo?.storage_temperature || "-"}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t('mediaPowder.lotDetails.totalReceived')}</p>
                <p className="text-sm font-black text-slate-700">{selectedLotSummary?.quantity || "0 g"}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t('mediaPowder.lotDetails.currentBalance')}</p>
                <p className="text-sm font-black text-emerald-600">{selectedLotSummary?.stock || "0 g"}</p>
              </div>
            </div>
          </div>

          {/* Life Cycle Timeline */}
          <div className="bg-[#2D3E50] rounded-2xl p-4 text-white shadow-lg relative overflow-hidden">
            <h4 className="text-[10px] font-black uppercase tracking-[0.2em] mb-8 text-white/50 border-b border-white/10 pb-3">{t('mediaPowder.lotDetails.lifecycleTitle')}</h4>
            <div className="space-y-2 relative z-10">
              {lifecycleEvents.map((event, i, arr) => (
                <div key={i} className="flex gap-4 relative">
                  {i !== arr.length - 1 && (
                    <div className="absolute left-[9px] top-5 bottom-[-24px] w-0.5 bg-white/10"></div>
                  )}
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${event.status === 'Completed' ? 'bg-emerald-500 border-emerald-400' : 'bg-orange-500 border-orange-400 animate-pulse'}`}>
                    <span className="material-symbols-outlined text-[20px] font-black">{event.status === 'Completed' ? 'check' : 'sync'}</span>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <p className="text-[10px] font-black uppercase tracking-widest text-white">{event.event}</p>
                    <p className="text-[8px] font-bold text-white/40 font-mono uppercase">{event.date} • {event.user}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
  );
};

export default MediaPowderSpecifications;